import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getTimeline, createTimelineEntry, updateTimelineEntry, deleteTimelineEntry } from '../apiTimeline';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  kindInfo, followUpLabel, groupForPanel, parseLinks, authorLabel, validateDraft, todayIso, addDays,
} from './timeline';

/*
 * One domain's notes / changes / follow-ups. On the domain page it shows every
 * entry; in the page editor (`page` set) only that page's entries plus the
 * site-wide ones, and new entries default to the page.
 *   <TimelinePanel domainId="x" />                  domain page
 *   <TimelinePanel domainId="x" page="a/b" compact /> page editor sidebar
 */

const SHOWN = 12;

const emptyDraft = (kind, page) => ({
  kind, text: '', date: todayIso(), due_date: kind === 'follow-up' ? addDays(todayIso(), 14) : '',
  links: '', page: page || '', pageScope: !!page,
});

const editorPath = (domainId, slug) => `/domain/${encodeURIComponent(domainId)}/pages/${encodeURIComponent(slug)}/edit`;

const Entry = ({ e, domainId, page, onToggle, onDelete, busy }) => {
  const k = kindInfo(e.kind);
  const fu = followUpLabel(e);
  return (
    <li className="timeline-entry" style={{ padding: '10px 0', borderTop: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <Badge state={k.badge}>{k.label}</Badge>
        {e.author?.type === 'agent' && <Badge state="plain" title={authorLabel(e.author)}>Agent</Badge>}
        {fu && <Badge state={fu.badge}>{fu.text}</Badge>}
        <span className="muted">{e.date}</span>
        <span className="muted">·</span>
        {e.scope === 'page' && e.page != null ? (
          page ? <span className="muted">this page</span>
            : <Link to={editorPath(domainId, e.page)} className="mono" style={{ fontSize: 12 }}>/{e.page}</Link>
        ) : <span className="muted">site-wide</span>}
      </div>
      <div className="timeline-text" style={{ fontSize: 14, marginTop: 4, overflowWrap: 'anywhere' }}>
        <ReactMarkdown>{e.text}</ReactMarkdown>
      </div>
      {e.links && e.links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, marginBottom: 4 }}>
          {e.links.map((l, i) => (l.url
            ? <a key={i} href={l.url} target="_blank" rel="noopener noreferrer">{l.label || l.url} ↗</a>
            : <span key={i} className="mono muted">{l.label || l.ref}</span>))}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12 }}>
        <span className="muted" title={e.created_at}>
          {e.author?.type === 'agent' ? 'Logged by ' : ''}{authorLabel(e.author)}
        </span>
        <span style={{ flex: 1 }} />
        {e.kind === 'follow-up' && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onToggle(e)}>
            {e.status === 'done' ? 'Reopen' : 'Mark done'}
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onDelete(e)} aria-label="Delete entry">Delete</button>
      </div>
    </li>
  );
};

export default function TimelinePanel({ domainId, page = null, compact = false, onEntriesChange }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getTimeline(domainId, { page });
      setEntries(data.entries || []);
      setError(null);
      if (onEntriesChange) onEntriesChange(data.entries || []);
    } catch (err) {
      setEntries([]);
      setError(err.response?.data?.detail || 'Could not load the timeline.');
    }
  }, [domainId, page, onEntriesChange]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = async (ev) => {
    ev.preventDefault();
    const msg = validateDraft(draft);
    if (msg) { setError(msg); return; }
    setSaving(true);
    try {
      const body = {
        kind: draft.kind, text: draft.text, links: parseLinks(draft.links),
        page: draft.pageScope ? (page || draft.page || null) : null,
      };
      if (draft.kind === 'follow-up') body.due_date = draft.due_date;
      else if (draft.date) body.date = draft.date;
      await createTimelineEntry(domainId, body);
      setDraft(null);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not save the entry.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (e) => {
    setBusyId(e.id);
    try {
      await updateTimelineEntry(domainId, e.id, { status: e.status === 'done' ? 'open' : 'done' });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update the follow-up.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = (e) => setConfirm({
    entry: e,
    onConfirm: async () => {
      setConfirm(null);
      setBusyId(e.id);
      try {
        await deleteTimelineEntry(domainId, e.id);
        await load();
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not delete the entry.');
      } finally {
        setBusyId(null);
      }
    },
  });

  const { open, rest, overdue } = groupForPanel(entries || []);
  const list = [...open, ...rest];
  const shown = showAll ? list : list.slice(0, compact ? 6 : SHOWN);

  return (
    <div className="timeline-panel">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraft(emptyDraft('note', page))}>Add note</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraft(emptyDraft('follow-up', page))}>Add follow-up</button>
        {overdue > 0 && <Badge state="danger">{overdue} overdue</Badge>}
      </div>

      {error && <div className="small" role="alert" style={{ color: 'var(--color-danger)', marginBottom: 8 }}>{error}</div>}

      {draft && (
        <form onSubmit={submit} className="timeline-form" style={{ display: 'grid', gap: 8, padding: '10px 0', borderTop: '1px solid var(--color-border)' }}>
          {draft.kind !== 'follow-up' && (
            <div role="group" aria-label="Kind" className="chip-group">
              {['note', 'change'].map((k) => (
                <button key={k} type="button" className={`chip${draft.kind === k ? ' active' : ''}`} aria-pressed={draft.kind === k}
                        onClick={() => set('kind', k)}>{kindInfo(k).label}</button>
              ))}
            </div>
          )}
          <label className="sr-only" htmlFor={`tl-text-${domainId}`}>Text</label>
          <textarea id={`tl-text-${domainId}`} className="input" rows={compact ? 3 : 4} value={draft.text} autoFocus
                    onChange={(e) => set('text', e.target.value)}
                    placeholder={draft.kind === 'follow-up' ? 'What to check, and what would count as working (markdown)' : 'What happened or what to remember (markdown)'} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 13 }}>
            {draft.kind === 'follow-up' ? (
              <label>Due <input type="date" className="input" style={{ width: 'auto' }} value={draft.due_date} required
                                onChange={(e) => set('due_date', e.target.value)} /></label>
            ) : (
              <label>Date <input type="date" className="input" style={{ width: 'auto' }} value={draft.date}
                                 onChange={(e) => set('date', e.target.value)} /></label>
            )}
            {page ? (
              <label><input type="checkbox" checked={draft.pageScope} onChange={(e) => set('pageScope', e.target.checked)} /> This page only</label>
            ) : (
              <label style={{ flex: 1, minWidth: 180 }}>
                <input type="text" className="input" value={draft.page} placeholder="Page slug (blank = whole site)"
                       aria-label="Page slug, blank for the whole site"
                       onChange={(e) => setDraft((d) => ({ ...d, page: e.target.value, pageScope: !!e.target.value.trim() }))} />
              </label>
            )}
          </div>
          <textarea className="input" rows={2} value={draft.links} onChange={(e) => set('links', e.target.value)}
                    aria-label="Links" placeholder={'Links, one per line: "DAM #85 | https://github.com/…" (optional)'} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : `Save ${kindInfo(draft.kind).label.toLowerCase()}`}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setDraft(null); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}

      {entries === null ? <Spinner size={20} label="Loading timeline…" /> : list.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          {page ? 'No notes for this page or the site yet.' : 'No notes, changes or follow-ups yet. Log a change when you make one, and add a follow-up to check it.'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {shown.map((e) => (
            <Entry key={e.id} e={e} domainId={domainId} page={page} onToggle={toggle} onDelete={remove} busy={busyId === e.id} />
          ))}
        </ul>
      )}
      {list.length > shown.length && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAll(true)}>Show all {list.length}</button>
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Delete this entry?"
        message={confirm ? `"${(confirm.entry.text || '').slice(0, 80)}" will be removed from the timeline.` : ''}
        confirmLabel="Delete"
        danger
        onConfirm={confirm ? confirm.onConfirm : () => {}}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
